import React, { useEffect, useRef } from "react";
import "./ProductsOverview.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import iconPlus from "../assets/icon_plus.webp";
import iconSR from "../assets/icon_sr.webp";
import iconReM from "../assets/icon_rem.webp";
import bgPLUS from "../assets/bg_overview_plus.webp";
import bgSR from "../assets/bg_overview_sr.webp";
import bgREM from "../assets/bg_overview_rem.webp";

gsap.registerPlugin(ScrollTrigger);

const ProductsOverview: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLAnchorElement[]>([]);

  useEffect(() => {
    // 初期状態を確実に設定
    gsap.set(cardsRef.current, { opacity: 1, y: 0 });

    cardsRef.current.forEach((card, index) => {
      gsap.fromTo(
        card,
        {
          opacity: 0,
          y: 80,
        },
        {
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none none",
            once: true,
          },
          opacity: 1,
          y: 0,
          duration: 0.8,
          delay: index * 0.2,
          ease: "power3.out",
        },
      );
    });
  }, []);

  const addToRefs = (el: HTMLAnchorElement) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  return (
    <section
      className="products-overview"
      ref={sectionRef}
      aria-label="製品ラインアップ"
    >
      <div className="products-container">
        <h2 className="section-title">製品ラインアップ</h2>

        <div className="products-grid">
          <a
            id="plus-detail"
            href="#screen-plus"
            className="product-card"
            ref={addToRefs}
            aria-label="eComicScreen+™の詳細を見る"
            style={{
              backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.8) 0%,rgba(255, 255, 255, 0.97) 15%, rgba(255, 255, 255, 0.97) 90%, rgba(255, 255, 255, 0.9) 100%), url(${bgPLUS})`,
            }}
          >
            <div className="card-icon">
              <img
                className="logo-icon"
                src={iconPlus}
                alt="eComicScreen+™ アイコン"
              />
            </div>
            <h3 className="product-plus">eComicScreen+™</h3>
            <p className="card-tagline">モアレ抑制技術</p>
            <p className="card-overview">『大きな画像をキレイに縮小』</p>
            <p className="card-description">
              モアレを抑制しつつ
              <br />
              印刷用のデータから高品質の
              <br />
              電子コミック画像を作成
            </p>
            <div className="card-arrow" aria-hidden="true">
              →
            </div>
          </a>

          <a
            id="sr-detail"
            href="#sr"
            className="product-card"
            ref={addToRefs}
            aria-label="eComicSR®の詳細を見る"
            style={{
              backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.85) 0%,rgba(255, 255, 255, 0.97) 15%, rgba(255, 255, 255, 0.97) 90%, rgba(255, 255, 255, 0.9) 100%), url(${bgSR})`,
            }}
          >
            <div className="card-icon">
              <img
                className="logo-icon"
                src={iconSR}
                alt="eComicSR® アイコン"
              />
            </div>
            <h3 className="product-sr">eComicSR®</h3>
            <p className="card-tagline">超解像技術</p>
            <p className="card-overview">『小さな画像をキレイに拡大』</p>
            <p className="card-description">
              過去に制作した電子コミック画像を
              <br />
              超解像技術でデジタルリマスター
            </p>
            <div className="card-arrow" aria-hidden="true">
              →
            </div>
          </a>

          <a
            id="rem-detail"
            href="#rem"
            className="product-card"
            ref={addToRefs}
            aria-label="eComicScreen ReM™の詳細を見る"
            style={{
              backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.8) 0%,rgba(255, 255, 255, 0.97) 15%, rgba(255, 255, 255, 0.97) 90%, rgba(255, 255, 255, 0.9) 100%), url(${bgREM})`,
            }}
          >
            <div className="card-icon">
              <img
                className="logo-icon"
                src={iconReM}
                alt="eComicScreen ReM™ アイコン"
              />
            </div>
            <h3 className="product-rem">eComicScreen ReM™</h3>
            <p className="card-tagline">トーン除去技術</p>
            <p className="card-overview">『スクリーントーンをキレイに除去』</p>
            <p className="card-description">
              まんが画像のカラー化で手間のかかる
              <br />
              スクリーントーン除去作業を超効率化
            </p>
            <div className="card-arrow" aria-hidden="true">
              →
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProductsOverview;
